import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  CheckCircle2,
  CreditCard,
  Database,
  KeyRound,
  Lock,
  Mail,
  RefreshCcw,
  Save,
  Server,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { toast } from "sonner";

import { CreditAmount } from "@/components/genrl/CreditAmount";
import { formatMicros } from "@/lib/genrl";
import {
  PERMISSION_LABELS,
  ROLE_HINTS,
  ROLE_LABELS,
  ROLE_PERMISSION_OPTIONS,
  emptyWorkspace,
  formatPermissions,
  getRolePermissions,
  hasWorkspacePermission,
  inviteWorkspaceMember,
  normalizeRole,
  normalizeRolePermissions,
  readWorkspace,
  readCurrentWorkspace,
  readWorkspaceOrg,
  removeWorkspaceMember,
  sendWorkspaceMemberCredits,
  setWorkspaceMemberBudget,
  updateWorkspaceMemberRole,
  updateWorkspaceProfile,
  updateWorkspaceRolePermissions,
  type WorkspacePermission,
  type WorkspacePublic,
  type WorkspaceCurrentResponse,
  type WorkspaceEntitlement,
  type WorkspaceOrgResponse,
  type WorkspaceRole,
  workspaceErrorMessage,
} from "@/lib/workspace";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/org")({
  head: () => ({
    meta: [
      { title: "Org Admin · GENRL" },
      {
        name: "description",
        content: "Workspace plan, entitlements, members, providers, and accounting controls.",
      },
    ],
  }),
  component: OrgAdminPage,
});

const CORE_ENTITLEMENTS = [
  "genrl_providers_enabled",
  "wallet_enabled",
  "quotes_enabled",
  "workloads_enabled",
  "storage_enabled",
  "persistent_runtimes_enabled",
  "custom_providers_enabled",
  "provider_network_publish_enabled",
] as const;

function OrgAdminPage() {
  const [current, setCurrent] = useState<WorkspaceCurrentResponse | null>(null);
  const [org, setOrg] = useState<WorkspaceOrgResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>("viewer");
  const [editingRole, setEditingRole] = useState<WorkspaceRole>("viewer");
  const [roleEditorOpen, setRoleEditorOpen] = useState(false);
  const [budgetDrafts, setBudgetDrafts] = useState<Record<string, { limitCredits: string; sendCredits: string }>>({});
  const [busy, setBusy] = useState("");

  async function load(nextRefreshing = false) {
    if (nextRefreshing) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const currentPayload = await readCurrentWorkspace();
      setCurrent(currentPayload);
      if (currentPayload.canManageOrg) {
        const orgPayload = await readWorkspaceOrg();
        setOrg(orgPayload);
      } else {
        setOrg(null);
      }
    } catch (err) {
      setError(workspaceErrorMessage(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  function applyWorkspace(nextWorkspace: WorkspacePublic) {
    setCurrent((payload) => payload ? { ...payload, workspace: nextWorkspace } : payload);
    setOrg((payload) => payload ? { ...payload, workspace: nextWorkspace } : payload);
  }

  function patchWorkspace(patch: Partial<WorkspacePublic>) {
    applyWorkspace({ ...(org?.workspace || current?.workspace || emptyWorkspace()), ...patch });
  }

  useEffect(() => {
    if (!org?.memberUsage?.length) return;
    setBudgetDrafts((drafts) => {
      let changed = false;
      const next = { ...drafts };
      for (const usage of org.memberUsage) {
        const key = usage.memberId || usage.email;
        if (!key || next[key]) continue;
        next[key] = {
          limitCredits: creditsInputFromMicros(usage.budgetLimitMicros || 0),
          sendCredits: "",
        };
        changed = true;
      }
      return changed ? next : drafts;
    });
  }, [org?.memberUsage]);

  function patchBudgetDraft(memberKey: string, patch: Partial<{ limitCredits: string; sendCredits: string }>) {
    setBudgetDrafts((drafts) => ({
      ...drafts,
      [memberKey]: {
        limitCredits: drafts[memberKey]?.limitCredits || "",
        sendCredits: drafts[memberKey]?.sendCredits || "",
        ...patch,
      },
    }));
  }

  async function reloadWorkspaceFallback() {
    try {
      const payload = await readWorkspace();
      applyWorkspace(payload.workspace);
    } catch {
      // The visible error toast from the original action is enough.
    }
  }

  async function saveWorkspaceProfile(workspace: WorkspacePublic) {
    if (!hasWorkspacePermission(workspace, "manage_workspace")) {
      toast.error("You do not have permission to change workspace profile details.");
      return;
    }
    setBusy("profile");
    try {
      const payload = await updateWorkspaceProfile({
        companyName: workspace.companyName.trim() || workspace.name || "Company workspace",
        billingEmail: workspace.billingEmail.trim(),
      });
      applyWorkspace(payload.workspace);
      toast.success("Workspace profile saved");
    } catch (err) {
      toast.error(workspaceErrorMessage(err));
    } finally {
      setBusy("");
    }
  }

  async function inviteMember(workspace: WorkspacePublic) {
    if (!hasWorkspacePermission(workspace, "invite_users")) {
      toast.error("You do not have permission to invite workspace users.");
      return;
    }
    const targetEmail = inviteEmail.trim().toLowerCase();
    const targetName = inviteName.trim() || targetEmail.split("@")[0] || "Invited user";
    if (!targetEmail || !targetEmail.includes("@")) {
      toast.error("Enter a valid email address.");
      return;
    }
    if (workspace.members.some((member) => member.email.toLowerCase() === targetEmail)) {
      toast.error("That user is already in this workspace.");
      return;
    }
    setBusy("invite");
    try {
      const payload = await inviteWorkspaceMember({
        name: targetName,
        email: targetEmail,
        role: inviteRole,
      });
      applyWorkspace(payload.workspace);
      setInviteName("");
      setInviteEmail("");
      setInviteRole("viewer");
      toast.success("User invite added");
    } catch (err) {
      toast.error(workspaceErrorMessage(err));
    } finally {
      setBusy("");
    }
  }

  async function updateMemberRole(workspace: WorkspacePublic, memberId: string, role: WorkspaceRole) {
    if (!hasWorkspacePermission(workspace, "manage_roles")) {
      toast.error("You do not have permission to change member roles.");
      return;
    }
    setBusy(`role:${memberId}`);
    try {
      const payload = await updateWorkspaceMemberRole(memberId, role);
      applyWorkspace(payload.workspace);
      toast.success("Member role updated");
    } catch (err) {
      toast.error(workspaceErrorMessage(err));
    } finally {
      setBusy("");
    }
  }

  async function removeMember(workspace: WorkspacePublic, memberId: string) {
    if (!hasWorkspacePermission(workspace, "manage_roles")) {
      toast.error("You do not have permission to remove workspace members.");
      return;
    }
    setBusy(`remove:${memberId}`);
    try {
      const payload = await removeWorkspaceMember(memberId);
      applyWorkspace(payload.workspace);
      toast.success("Member removed");
    } catch (err) {
      toast.error(workspaceErrorMessage(err));
    } finally {
      setBusy("");
    }
  }

  async function saveMemberBudget(memberKey: string, memberId: string) {
    const limitCredits = parseCreditInput(budgetDrafts[memberKey]?.limitCredits || "");
    if (limitCredits <= 0) {
      toast.error("Enter a positive budget amount.");
      return;
    }
    setBusy(`budget:${memberId}`);
    try {
      const payload = await setWorkspaceMemberBudget({
        memberId,
        limitCredits,
        sourceType: "owner_wallet",
        status: "active",
      });
      setOrg(payload);
      applyWorkspace(payload.workspace);
      toast.success("Member budget updated");
    } catch (err) {
      toast.error(workspaceErrorMessage(err));
    } finally {
      setBusy("");
    }
  }

  async function sendMemberCredits(memberKey: string, memberId: string, email: string) {
    const amountCredits = parseCreditInput(budgetDrafts[memberKey]?.sendCredits || "");
    if (amountCredits <= 0) {
      toast.error("Enter a positive credit amount.");
      return;
    }
    setBusy(`send:${memberId}`);
    try {
      const payload = await sendWorkspaceMemberCredits({
        memberId,
        email,
        amountCredits,
      });
      setOrg(payload);
      applyWorkspace(payload.workspace);
      patchBudgetDraft(memberKey, { sendCredits: "" });
      toast.success("Credits sent");
    } catch (err) {
      toast.error(workspaceErrorMessage(err));
    } finally {
      setBusy("");
    }
  }

  async function toggleRolePermission(
    workspace: WorkspacePublic,
    role: WorkspaceRole,
    permission: WorkspacePermission,
  ) {
    if (role === "owner") {
      toast.info("Owner permissions are always full.");
      return;
    }
    if (!hasWorkspacePermission(workspace, "manage_roles")) {
      toast.error("You do not have permission to edit role permissions.");
      return;
    }
    const currentPermissions = getRolePermissions(workspace.rolePermissions, role);
    const nextPermissions = currentPermissions.includes(permission)
      ? currentPermissions.filter((item) => item !== permission)
      : [...currentPermissions, permission];
    const nextRolePermissions = normalizeRolePermissions({
      ...workspace.rolePermissions,
      [role]: nextPermissions,
    });
    const optimisticWorkspace = { ...workspace, rolePermissions: nextRolePermissions };
    applyWorkspace(optimisticWorkspace);
    setBusy(`permission:${role}:${permission}`);
    try {
      const payload = await updateWorkspaceRolePermissions(nextRolePermissions);
      applyWorkspace(payload.workspace);
    } catch (err) {
      toast.error(workspaceErrorMessage(err));
      await reloadWorkspaceFallback();
    } finally {
      setBusy("");
    }
  }

  useEffect(() => {
    void load(false);
  }, []);

  const entitlements = org?.entitlements || current?.entitlements || {};
  const entitlementRows = useMemo(
    () => CORE_ENTITLEMENTS.map((key) => ({ key, ...normalizeEntitlement(entitlements[key]) })),
    [entitlements],
  );

  if (loading) {
    return (
      <div className="compute-wallet-page compute-org-page">
        <div className="compute-wallet-shell compute-storage-wallet-shell">
          <main className="compute-wallet-main compute-storage-wallet-main">
            <OrgHeader refreshing={refreshing} refreshDisabled onRefresh={() => void load(true)} />
            <section className="compute-org-panel compute-org-loading">
              Loading workspace controls
            </section>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="compute-wallet-page compute-org-page">
        <div className="compute-wallet-shell compute-storage-wallet-shell">
          <main className="compute-wallet-main compute-storage-wallet-main">
            <OrgHeader refreshing={refreshing} onRefresh={() => void load(true)} />
            <section className="compute-org-panel compute-org-empty">
              <AlertTriangle className="h-5 w-5" />
              <div>
                <strong>{error}</strong>
                <button
                  type="button"
                  onClick={() => void load(false)}
                  className="compute-org-retry-button"
                >
                  Retry <RefreshCcw className="h-4 w-4" />
                </button>
              </div>
            </section>
          </main>
        </div>
      </div>
    );
  }

  if (!current?.canManageOrg) {
    return (
      <div className="compute-wallet-page compute-org-page">
        <div className="compute-wallet-shell compute-storage-wallet-shell">
          <main className="compute-wallet-main compute-storage-wallet-main">
            <OrgHeader refreshing={refreshing} onRefresh={() => void load(true)} />
            <section className="compute-org-panel compute-org-locked">
              <Lock className="h-6 w-6" />
              <div>
                <span>Owner access required</span>
                <h2>{current?.workspace.name || "Workspace admin"}</h2>
                <p>
                  Only workspace owners can view full organisation controls, billing state, provider
                  publication, and security activity.
                </p>
              </div>
              <Link to="/settings" hash="workspace">
                Workspace settings <ArrowRight className="h-4 w-4" />
              </Link>
            </section>
          </main>
        </div>
      </div>
    );
  }

  const workspace = org?.workspace || current.workspace;
  const plan = org?.plan || current.plan;
  const stats = org?.stats;
  const providers = org?.providers || [];
  const events = org?.securityEvents || [];
  const memberUsage = org?.memberUsage || [];
  const canManageWorkspace = hasWorkspacePermission(workspace, "manage_workspace");
  const canInviteUsers = hasWorkspacePermission(workspace, "invite_users");
  const canManageRoles = hasWorkspacePermission(workspace, "manage_roles");

  return (
    <div className="compute-wallet-page compute-org-page">
      <div className="compute-wallet-shell compute-storage-wallet-shell">
        <main className="compute-wallet-main compute-storage-wallet-main">
          <OrgHeader refreshing={refreshing} onRefresh={() => void load(true)} />

          <section className="compute-org-hero" aria-label="Workspace overview">
            <div>
              <span>{workspace.workspaceType}</span>
              <h1>{workspace.name || workspace.companyName}</h1>
              <p>{workspace.id}</p>
            </div>
            <div className="compute-org-plan-badge">
              <CreditCard className="h-5 w-5" />
              <span>{plan.name}</span>
              <small>{plan.billingStatus.replaceAll("_", " ")}</small>
            </div>
          </section>

          <section
            className="compute-storage-summary compute-org-summary"
            aria-label="Organisation summary"
          >
            <OrgStat
              icon={Wallet}
              label="Available"
              value={formatMicros(stats?.availableCreditMicros || 0, { suffix: "" })}
              hint="credits ready"
            />
            <OrgStat
              icon={ShieldCheck}
              label="Reserved"
              value={formatMicros(stats?.reservedCreditMicros || 0, { suffix: "" })}
              hint="reserved credits"
            />
            <OrgStat
              icon={Server}
              label="Providers"
              value={`${stats?.activeProviderCount || 0} / ${stats?.providerCount || 0}`}
              hint={`${stats?.networkProviderCount || 0} network`}
            />
            <OrgStat
              icon={Activity}
              label="Live"
              value={`${stats?.activeRuntimeCount || 0}`}
              hint={`${stats?.storageObjectCount || 0} storage objects`}
            />
          </section>

          <section className="compute-org-grid">
            <section className="compute-org-panel compute-org-wide">
              <PanelHeader
                icon={Users}
                title="Workspace profile and members"
                action={<span>{workspace.members.length} members</span>}
              />
              <div className="compute-org-profile-grid">
                <OrgField label="Company name" icon={Users}>
                  <OrgTextInput
                    value={workspace.companyName}
                    onChange={(value) => patchWorkspace({ companyName: value, name: value || workspace.name })}
                    placeholder="Company workspace"
                    disabled={!canManageWorkspace}
                  />
                </OrgField>
                <OrgField label="Billing and legal email" icon={Mail}>
                  <OrgTextInput
                    value={workspace.billingEmail}
                    onChange={(value) => patchWorkspace({ billingEmail: value })}
                    placeholder="legal@company.com"
                    disabled={!canManageWorkspace}
                  />
                </OrgField>
                <button
                  type="button"
                  onClick={() => void saveWorkspaceProfile(workspace)}
                  disabled={!canManageWorkspace || busy === "profile"}
                  className="compute-org-action-button"
                >
                  <Save className="h-4 w-4" />
                  {busy === "profile" ? "Saving" : "Save workspace"}
                </button>
              </div>

              <div className="compute-org-invite-panel">
                <div>
                  <span>Invite user</span>
                  <p>Add a team member to this company account and choose their starting role.</p>
                </div>
                <div className="compute-org-invite-grid">
                  <OrgTextInput
                    value={inviteName}
                    onChange={setInviteName}
                    placeholder="Name"
                    disabled={!canInviteUsers || busy === "invite"}
                  />
                  <OrgTextInput
                    value={inviteEmail}
                    onChange={setInviteEmail}
                    placeholder="email@company.com"
                    disabled={!canInviteUsers || busy === "invite"}
                  />
                  <OrgRoleSelect
                    value={inviteRole}
                    onChange={setInviteRole}
                    disabled={!canInviteUsers || busy === "invite"}
                  />
                  <button
                    type="button"
                    onClick={() => void inviteMember(workspace)}
                    disabled={!canInviteUsers || busy === "invite"}
                    className="compute-org-action-button"
                  >
                    <UserPlus className="h-4 w-4" />
                    {busy === "invite" ? "Inviting" : "Invite"}
                  </button>
                </div>
              </div>

              <div className="compute-org-member-list">
                {workspace.members.length === 0 ? (
                  <div className="compute-org-empty-inline">No workspace members yet.</div>
                ) : (
                  workspace.members.map((member) => {
                    const memberBusy = busy.endsWith(`:${member.id}`);
                    return (
                      <div key={member.id} className="compute-org-member-row">
                        <span>{initials(member.name || member.email)}</span>
                        <div>
                          <strong>{member.name || member.email}</strong>
                          <small>{member.email}</small>
                          <p>{formatPermissions(getRolePermissions(workspace.rolePermissions, member.role)) || "No permissions assigned"}</p>
                        </div>
                        <em>{member.status}</em>
                        <OrgRoleSelect
                          value={member.role}
                          onChange={(role) => void updateMemberRole(workspace, member.id, role)}
                          disabled={!canManageRoles || memberBusy}
                        />
                        <button
                          type="button"
                          onClick={() => void removeMember(workspace, member.id)}
                          disabled={!canManageRoles || memberBusy}
                          className="compute-org-secondary-button"
                        >
                          <Trash2 className="h-4 w-4" />
                          {busy === `remove:${member.id}` ? "Removing" : "Remove"}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="compute-org-role-panel">
                <button
                  type="button"
                  onClick={() => setRoleEditorOpen((open) => !open)}
                  aria-expanded={roleEditorOpen}
                  className="compute-org-role-toggle"
                >
                  <span>
                    <ShieldCheck className="h-4 w-4" />
                    Editable roles
                  </span>
                  <ChevronDown className={cn("h-4 w-4", roleEditorOpen && "rotate-180")} />
                </button>
                {roleEditorOpen && (
                  <div className="compute-org-role-editor">
                    <div className="compute-org-role-editor-head">
                      <OrgField label="Role to edit" icon={ShieldCheck}>
                        <OrgRoleSelect
                          value={editingRole}
                          onChange={setEditingRole}
                          disabled={!canManageRoles}
                        />
                      </OrgField>
                      <div>
                        <strong>{ROLE_LABELS[editingRole]}</strong>
                        <p>{ROLE_HINTS[editingRole]}</p>
                      </div>
                    </div>
                    <div className="compute-org-permission-grid">
                      {ROLE_PERMISSION_OPTIONS.map((permission) => {
                        const checked = getRolePermissions(workspace.rolePermissions, editingRole).includes(permission);
                        const permissionBusy = busy === `permission:${editingRole}:${permission}`;
                        return (
                          <label
                            key={permission}
                            className={cn("compute-org-permission-row", editingRole === "owner" && "is-disabled")}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => void toggleRolePermission(workspace, editingRole, permission)}
                              disabled={!canManageRoles || permissionBusy || editingRole === "owner"}
                            />
                            <span>{PERMISSION_LABELS[permission]}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="compute-org-panel compute-org-wide">
              <PanelHeader
                icon={Wallet}
                title="Budgets and credit transfers"
                action={<span>{formatMicros(stats?.budgetRemainingMicros || 0, { suffix: "" })} budget left</span>}
              />
              {memberUsage.length === 0 ? (
                <div className="compute-org-empty-inline">No active members to budget yet.</div>
              ) : (
                <div className="compute-org-budget-table">
                  <div>
                    <span>Member</span>
                    <span>Owner budget</span>
                    <span>Funded</span>
                    <span>Wallet</span>
                    <span>Set budget</span>
                    <span>Send to email</span>
                  </div>
                  {memberUsage.map((usage) => {
                    const memberKey = usage.memberId || usage.email;
                    const draft = budgetDrafts[memberKey] || {
                      limitCredits: creditsInputFromMicros(usage.budgetLimitMicros || 0),
                      sendCredits: "",
                    };
                    const isSelf = usage.memberId === workspace.currentMemberId;
                    const canActOnMember = canManageWorkspace && !isSelf && usage.status === "active" && Boolean(usage.userId);
                    const memberBusy = busy === `budget:${usage.memberId}` || busy === `send:${usage.memberId}`;
                    return (
                      <div key={memberKey}>
                        <div>
                          <strong>{usage.name || usage.email}</strong>
                          <small>
                            {usage.email} · {isSelf ? "Owner" : ROLE_LABELS[normalizeRole(usage.role)]}
                          </small>
                        </div>
                        <span>
                          <CreditAmount micros={usage.budgetLimitMicros || 0} suffix="" muted />
                          <small>{(usage.budgetStatus || "none").replaceAll("_", " ")}</small>
                        </span>
                        <span>
                          <CreditAmount micros={usage.budgetFundedMicros || 0} suffix="" muted />
                          <small>{formatMicros(usage.budgetRemainingMicros || 0, { suffix: "" })} left</small>
                        </span>
                        <span>
                          <CreditAmount micros={usage.availableCreditMicros || 0} suffix="" muted />
                          <small>{formatMicros(usage.reservedCreditMicros || 0, { suffix: "" })} reserved</small>
                        </span>
                        <div className="compute-org-budget-control">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft.limitCredits}
                            onChange={(event) => patchBudgetDraft(memberKey, { limitCredits: event.target.value })}
                            disabled={!canActOnMember || memberBusy}
                            className="compute-org-input"
                            aria-label={`Budget credits for ${usage.email}`}
                          />
                          <button
                            type="button"
                            onClick={() => void saveMemberBudget(memberKey, usage.memberId)}
                            disabled={!canActOnMember || memberBusy}
                            className="compute-org-secondary-button"
                          >
                            Save
                          </button>
                        </div>
                        <div className="compute-org-budget-control">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft.sendCredits}
                            onChange={(event) => patchBudgetDraft(memberKey, { sendCredits: event.target.value })}
                            disabled={!canActOnMember || memberBusy}
                            className="compute-org-input"
                            aria-label={`Send credits to ${usage.email}`}
                          />
                          <button
                            type="button"
                            onClick={() => void sendMemberCredits(memberKey, usage.memberId, usage.email)}
                            disabled={!canActOnMember || memberBusy}
                            className="compute-org-action-button"
                            title={`Send credits to ${usage.email}`}
                          >
                            Send
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="compute-org-panel compute-org-wide">
              <PanelHeader
                icon={Activity}
                title="Usage by member"
                action={<span>{memberUsage.length} tracked</span>}
              />
              {memberUsage.length === 0 ? (
                <div className="compute-org-empty-inline">No member usage has been recorded yet.</div>
              ) : (
                <div className="compute-org-usage-table">
                  <div>
                    <span>Member</span>
                    <span>Spend</span>
                    <span>Reserved</span>
                    <span>Workloads</span>
                    <span>Runtime</span>
                    <span>Storage</span>
                    <span>Providers</span>
                    <span>Earned</span>
                    <span>Last active</span>
                  </div>
                  {memberUsage.map((usage) => (
                    <div key={usage.memberId || usage.email}>
                      <div>
                        <strong>{usage.name || usage.email}</strong>
                        <small>
                          {usage.email} · {ROLE_LABELS[normalizeRole(usage.role)]} · {usage.status}
                        </small>
                      </div>
                      <CreditAmount
                        micros={usage.spentCreditMicros || 0}
                        suffix=""
                        muted
                        className="compute-org-credit-cell"
                      />
                      <CreditAmount
                        micros={usage.reservedCreditMicros || 0}
                        suffix=""
                        muted
                        className="compute-org-credit-cell"
                      />
                      <span>
                        {usage.workloadCount || 0}
                        <small>{usage.activeWorkloadCount || 0} active</small>
                      </span>
                      <span>
                        {usage.activeRuntimeCount || 0}
                        <small>{usage.runtimeTurnCount || 0} turns</small>
                      </span>
                      <span>
                        {usage.storageObjectCount || 0}
                        <small>{formatBytes(usage.storageBytes || 0)}</small>
                      </span>
                      <span>
                        {usage.providerCount || 0}
                        <small>{usage.networkProviderCount || 0} network</small>
                      </span>
                      <CreditAmount
                        micros={usage.earnedCreditMicros || 0}
                        suffix=""
                        muted
                        className="compute-org-credit-cell"
                      />
                      <time>{formatWhen(usage.lastActivityAt || "")}</time>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="compute-org-panel">
              <PanelHeader
                icon={KeyRound}
                title="Entitlements"
                action={
                  <Link to="/settings" hash="pro">
                    Plan
                  </Link>
                }
              />
              <div className="compute-org-entitlement-list">
                {entitlementRows.map((row) => (
                  <div key={row.key} className="compute-org-entitlement-row">
                    {row.enabled ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <Lock className="h-4 w-4" />
                    )}
                    <span>{labelEntitlement(row.key)}</span>
                    <strong className={cn(row.enabled && "compute-org-enabled")}>
                      {row.enabled ? "Enabled" : "Locked"}
                    </strong>
                  </div>
                ))}
              </div>
            </section>

            <section className="compute-org-panel compute-org-wide">
              <PanelHeader
                icon={Server}
                title="Provider publication"
                action={<Link to="/store">Capacity</Link>}
              />
              {providers.length === 0 ? (
                <div className="compute-org-empty-inline">No providers are connected yet.</div>
              ) : (
                <div className="compute-org-provider-table">
                  <div>
                    <span>Provider</span>
                    <span>Status</span>
                    <span>Visibility</span>
                    <span>Earned</span>
                    <span>Pending</span>
                  </div>
                  {providers.map((provider) => (
                    <div key={provider.id}>
                      <strong>{provider.display_name || provider.provider_name}</strong>
                      <span>{provider.status}</span>
                      <span>{provider.visibility}</span>
                      <CreditAmount micros={provider.earned_credit_micros || 0} suffix="" muted />
                      <CreditAmount
                        micros={provider.owner_pending_payout_micros || 0}
                        suffix=""
                        muted
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="compute-org-panel">
              <PanelHeader icon={Database} title="Accounting" />
              <div className="compute-org-accounting-stack">
                <InfoRow
                  label="Total credits"
                  value={formatMicros(stats?.totalCreditMicros || 0, { suffix: "" })}
                />
                <InfoRow
                  label="Lifetime spent"
                  value={formatMicros(stats?.spentCreditMicros || 0, { suffix: "" })}
                />
                <InfoRow
                  label="Provider earned"
                  value={formatMicros(stats?.earnedCreditMicros || 0, { suffix: "" })}
                />
                <InfoRow
                  label="Pending payout"
                  value={formatMicros(stats?.pendingPayoutMicros || 0, { suffix: "" })}
                />
              </div>
            </section>

            <section className="compute-org-panel">
              <PanelHeader icon={ShieldCheck} title="Security activity" />
              {events.length === 0 ? (
                <div className="compute-org-empty-inline">No recent security events.</div>
              ) : (
                <div className="compute-org-event-list">
                  {events.map((event) => (
                    <div key={event.id} className="compute-org-event-row">
                      <span>{event.severity}</span>
                      <strong>{event.eventType.replaceAll("_", " ")}</strong>
                      <small>{formatWhen(event.createdAt)}</small>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </section>
        </main>
      </div>
    </div>
  );
}

function OrgHeader({
  refreshing,
  refreshDisabled = false,
  onRefresh,
}: {
  refreshing: boolean;
  refreshDisabled?: boolean;
  onRefresh: () => void;
}) {
  return (
    <header className="compute-wallet-header compute-storage-header">
      <div>
        <h1>ORG ADMIN</h1>
        <p>Workspace plan, entitlements, members, provider publication, and accounting.</p>
      </div>
      <div className="compute-wallet-header-actions compute-storage-header-actions">
        <button
          type="button"
          onClick={onRefresh}
          className="compute-wallet-circle-button"
          aria-busy={refreshing}
          aria-label="Refresh organisation"
          disabled={refreshDisabled || refreshing}
        >
          <RefreshCcw className={cn("h-[18px] w-[18px]", refreshing && "animate-spin")} />
        </button>
        <div>
          <strong>{refreshing ? "Refreshing" : "Synced"}</strong>
          <small>Workspace</small>
        </div>
      </div>
    </header>
  );
}

function OrgStat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="compute-storage-stat">
      <div>
        <Icon className="h-4 w-4" />
      </div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{hint}</small>
    </div>
  );
}

function PanelHeader({
  icon: Icon,
  title,
  action,
}: {
  icon: LucideIcon;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div className="compute-org-panel-header">
      <div>
        <Icon className="h-5 w-5" />
        <h2>{title}</h2>
      </div>
      {action}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="compute-org-info-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function OrgField({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon?: LucideIcon;
  children: ReactNode;
}) {
  return (
    <label className="compute-org-field">
      <span>
        {Icon ? <Icon className="h-3 w-3" /> : null}
        {label}
      </span>
      {children}
    </label>
  );
}

function OrgTextInput({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="compute-org-input"
    />
  );
}

function OrgRoleSelect({
  value,
  onChange,
  disabled,
}: {
  value: WorkspaceRole;
  onChange: (role: WorkspaceRole) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(normalizeRole(event.target.value))}
      disabled={disabled}
      className="compute-org-select"
    >
      <option value="viewer">{ROLE_LABELS.viewer}</option>
      <option value="admin">{ROLE_LABELS.admin}</option>
      <option value="owner">{ROLE_LABELS.owner}</option>
    </select>
  );
}

function normalizeEntitlement(entitlement?: WorkspaceEntitlement) {
  return {
    enabled: Boolean(entitlement?.enabled),
    limitValue: entitlement?.limitValue || "",
  };
}

function labelEntitlement(key: string) {
  return key
    .replaceAll("_enabled", "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function initials(value: string) {
  const clean = String(value || "").trim();
  if (!clean) return "WS";
  return clean
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function formatWhen(value: string) {
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return "Unknown";
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(time);
}

function creditsInputFromMicros(micros: number) {
  const credits = Number(micros || 0) / 1_000_000;
  if (!Number.isFinite(credits) || credits <= 0) return "";
  return credits.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

function parseCreditInput(value: string) {
  const credits = Number(String(value || "").trim());
  if (!Number.isFinite(credits) || credits <= 0) return 0;
  return credits;
}

function formatBytes(value: number) {
  const bytes = Number.isFinite(value) && value > 0 ? value : 0;
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let amount = bytes / 1024;
  let index = 0;
  while (amount >= 1024 && index < units.length - 1) {
    amount /= 1024;
    index += 1;
  }
  return `${amount >= 10 ? amount.toFixed(0) : amount.toFixed(1)} ${units[index]}`;
}
